import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useNotifications } from '@/state/use-notifications'
import { useSessionContext } from '@/state/session-context'
import { Bell, BellOff, AlertCircle, CheckCircle, Loader2, RefreshCw } from 'lucide-react'
import { toast } from 'react-hot-toast'

// TODO: This should come from environment config
const VAPID_PUBLIC_KEY = "BAbVLs3kRVeDouniEscPAQNjiw7wdBwDH2KM-jRMwohA9AvuTVie13M1h4tdh7kYwHnGH0LOxYWPIUsqQBhpLUo"

interface NotificationSettingsProps {
	onNotificationChange?: () => void
}

export function NotificationSettings({ onNotificationChange }: NotificationSettingsProps = {}) {
	const { session } = useSessionContext()
	const {
		permission,
		subscription,
		loading,
		error,
		syncing,
		isInitializing,
		isFullyEnabled,
		isEnabledInBackend,
		supportsNotifications,
		supportsPushManager,
		askPermission,
		ensureSubscription,
		unsubscribe,
		syncWithBackend
	} = useNotifications({ userId: session?.userId })

	const [isProcessing, setIsProcessing] = useState(false)

	const handleEnableNotifications = async () => {
		setIsProcessing(true)
		try {
			if (permission !== 'granted') {
				const result = await askPermission()
				if (result !== 'granted') {
					toast.error('Notification permission denied')
					return
				}
			}

			await ensureSubscription(VAPID_PUBLIC_KEY)
			await syncWithBackend()
			toast.success('Push notifications enabled!')
			onNotificationChange?.()
		} catch (err) {
			console.error('Failed to enable notifications:', err)
			toast.error('Failed to enable notifications')
		} finally {
			setIsProcessing(false)
		}
	}

	const handleSync = async () => {
		setIsProcessing(true)
		try {
			await syncWithBackend()
			toast.success('Synced with backend')
		} catch (err) {
			console.error('Failed to sync:', err)
			toast.error('Failed to sync')
		} finally {
			setIsProcessing(false)
		}
	}

	const handleDisableNotifications = async () => {
		setIsProcessing(true)
		try {
			await unsubscribe()
			toast.success('Push notifications disabled')
		} catch (err) {
			console.error('Failed to disable notifications:', err)
			toast.error('Failed to disable notifications')
		} finally {
			setIsProcessing(false)
		}
	}

	const getStatusIcon = () => {
		if (loading || isProcessing || syncing || isInitializing) {
			return <Loader2 className="h-4 w-4 animate-spin" />
		}
		if (error) {
			return <AlertCircle className="h-4 w-4 text-red-500" />
		}
		if (isFullyEnabled) {
			return <CheckCircle className="h-4 w-4 text-green-500" />
		}
		if (subscription && !isEnabledInBackend) {
			return <AlertCircle className="h-4 w-4 text-yellow-500" />
		}
		return <BellOff className="h-4 w-4 text-gray-400" />
	}

	const getStatusText = () => {
		if (isInitializing) return 'Loading...'
		if (syncing) return 'Syncing...'
		if (loading) return 'Enabling...'
		if (error) return 'Error setting up notifications'
		if (!supportsNotifications) return 'Notifications not supported'
		if (!supportsPushManager) return 'Push notifications not supported'
		if (permission === 'denied') return 'Notifications blocked'
		if (isFullyEnabled) return 'Notifications enabled'
		if (subscription && !isEnabledInBackend) return 'Out of sync - re-enable needed'
		if (permission === 'granted') return 'Ready to enable'
		return 'Not enabled'
	}

	const canEnable = supportsNotifications && supportsPushManager && !isFullyEnabled && permission !== 'denied' && !syncing && !isInitializing
	const canDisable = !!subscription && !syncing && !isInitializing
	const needsSync = subscription && !isEnabledInBackend && !syncing && !isInitializing

	if (!supportsNotifications) {
		return (
			<div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center">
				{/* <div className="w-full sm:w-32 sm:flex-shrink-0">
					<label className="block text-sm font-medium sm:text-end">Notifications</label>
				</div> */}
				<div className="flex items-center gap-2 text-sm text-gray-500">
					<AlertCircle className="h-4 w-4" />
					Push notifications not supported on this browser
				</div>
			</div>
		)
	}

	return (
		<div>
			<p className="text-sm mb-4">
				It is highly recommended that you enable push notifications. 
				If you do, we can notify you when you get added to a new group, or when you receive a new message, 
				even when hangaround.live is not open.
			</p>
			<div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center">
				{/* <div className="w-full sm:w-32 sm:flex-shrink-0">
					<label className="block text-sm font-medium sm:text-end">Notifications</label>
				</div> */}
				<div className="flex items-center gap-4">
					<div className="flex items-center gap-2">
						{getStatusIcon()}
						<span className="text-sm">{getStatusText()}</span>
					</div>
					<div className="flex gap-2">
						{canEnable && (
							<Button
								size="sm"
								variant="outline"
								onClick={handleEnableNotifications}
								disabled={isProcessing}
							>
								<Bell className="h-4 w-4 mr-1" />
								Enable
							</Button>
						)}
						{canDisable && (
							<Button
								size="sm"
								variant="outline"
								onClick={handleDisableNotifications}
								disabled={isProcessing}
							>
								<BellOff className="h-4 w-4 mr-1" />
								Disable
							</Button>
						)}
						{needsSync && (
							<Button
								size="sm"
								variant="outline"
								onClick={handleSync}
								disabled={isProcessing}
								className="text-yellow-600"
							>
								<RefreshCw className="h-4 w-4 mr-1" />
								Sync
							</Button>
						)}
					</div>
				</div>
			</div>

		</div>
	)
}